import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatchTracking } from '../hooks/useWatchTracking';
import { Play, Pause, Volume2, VolumeX, RotateCcw, RotateCw, PictureInPicture, Maximize } from 'lucide-react';

interface CustomVideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
  mimeType?: string;
  knownDuration?: number;
  /** Video ID used to probe duration server-side when it is not in DB yet. */
  videoId?: string;
  onDurationKnown?: (seconds: number) => void;
  /** Called when the video reaches the end */
  onEnded?: () => void;
  /** Called when playback starts (or resumes) */
  onPlay?: () => void;
  /**
   * Called once when the video can't be loaded (e.g. its short-lived link expired on a page
   * left open). The parent passes a fresh `src`; playback resumes where it was.
   */
  onSourceError?: () => void;
  /** Viewer insights: report plays and how far they get (with `videoId`; the server skips the owner). */
  trackViews?: boolean;
  /** Receives the current <video> so the page can read the time and seek (comments at a time). */
  videoElementRef?: React.MutableRefObject<HTMLVideoElement | null>;
  style?: React.CSSProperties;
}

const guessMimeType = (url: string, explicit?: string): string => {
  if (explicit && explicit.startsWith('video/')) return explicit;
  const lower = url.toLowerCase();
  if (lower.includes('.webm')) return 'video/webm';
  // Stream URLs have no extension — AIRecording default
  if (lower.includes('/api/videos/stream/')) return 'video/webm';
  return 'video/mp4';
};

/** Maximum realistic duration for a screen recording (12 hours). */
const MAX_SANE_DURATION_S = 3600 * 12;

/**
 * True when the duration value is a real, usable duration.
 * Rejects Infinity, NaN, non-positive values, and browser-clamped 1e10-seek
 * artifacts (which appear as huge finite numbers like 2 147 483 647).
 */
const isUsableDuration = (value: number): boolean =>
  Number.isFinite(value) && value > 0 && value < MAX_SANE_DURATION_S;

const CustomVideoPlayer = ({
  src,
  poster,
  className = '',
  mimeType,
  knownDuration,
  videoId,
  onDurationKnown,
  onEnded,
  onPlay,
  onSourceError,
  trackViews = false,
  videoElementRef,
  style,
}: CustomVideoPlayerProps): JSX.Element => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  useWatchTracking(videoRef, videoId, trackViews);
  const seekTimeRef = useRef(0);
  /**
   * Stores the last known-good duration so spurious durationchange events
   * (e.g. fired by the browser on fullscreen transitions) don't overwrite state.
   */
  const knownGoodDurationRef = useRef<number>(0);
  /**
   * Tracks the highest currentTime seen during playback.
   * Used to infer duration for WebM files when the browser reports Infinity,
   * without the disruptive 1e10-seek hack.
   */
  const lastKnownTimeRef = useRef<number>(0);
  /**
   * What the current source taught us, kept when the listeners are set up again (on every seek):
   * its real length once known, and a stored length that playback proved wrong.
   */
  const learnedRef = useRef<{ src: string; seconds: number; wrongHint: number }>({ src: '', seconds: 0, wrongHint: 0 });
  /** A seek past the loaded part of an index-less WebM, being turned into a read-through (see below). */
  const scanRef = useRef<{ target: number; resume: boolean; landing: boolean } | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(() => {
    const hint = knownDuration != null ? Number(knownDuration) : 0;
    return hint > 0 && hint !== 60 ? hint : 0;
  });
  const [volume, setVolume] = useState<number>(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  const getActiveDuration = useCallback((): number => {
    const video = videoRef.current;
    const fromElement = video?.duration;
    // isUsableDuration rejects both Infinity AND browser-clamped 1e10 artifacts (≈2,147,483,647 s)
    // which appear as huge finite numbers and would otherwise pass a simple isFinite() check.
    if (fromElement && isUsableDuration(fromElement)) return fromElement;
    if (duration > 0) return duration;
    return 0;
  }, [duration]);

  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) {
      return '0:00';
    }
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      // Restart from the beginning when the video has ended.
      // video.ended is the reliable DOM property; we also check against the
      // known good duration for cases where the browser still reports Infinity.
      if (video.ended) {
        video.currentTime = 0;
      } else {
        const knownDur = knownGoodDurationRef.current;
        if (knownDur > 0 && video.currentTime >= knownDur - 0.1) {
          video.currentTime = 0;
        }
      }
      video.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const getProgressPercentage = useCallback(() => {
    const knownDur = getActiveDuration();
    // Prefer the actual total duration when available
    const ref = knownDur > 0 ? knownDur : lastKnownTimeRef.current;
    if (!ref || ref <= 0) return 0;
    const result = Math.min(100, Math.max(0, (currentTime / ref) * 100));
    return Number.isNaN(result) ? 0 : result;
  }, [currentTime, getActiveDuration]);

  const seekToRatio = useCallback((ratio: number, commit: boolean) => {
    const video = videoRef.current;
    if (!video || !progressRef.current) return;

    const knownDur = getActiveDuration();
    // Allow seeking within already-played range when total is not yet known
    const ref = knownDur > 0 ? knownDur : lastKnownTimeRef.current;
    if (!ref || ref <= 0 || !Number.isFinite(ref)) return;

    const clamped = Math.max(0, Math.min(1, ratio));
    const newTime = clamped * ref;
    if (!Number.isFinite(newTime)) return;

    seekTimeRef.current = newTime;
    setCurrentTime(newTime);
    if (commit) {
      video.currentTime = newTime;
    }
  }, [getActiveDuration]);

  const handleScrub = (e: React.MouseEvent<HTMLDivElement>, commit = true) => {
    if (!progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    seekToRatio(pos, commit);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isSeeking || !progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    seekToRatio(pos, false);
  }, [isSeeking, seekToRatio]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const skip = (seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    // When duration is unknown (WebM Infinity), clamp only on the lower bound
    // so forward/backward skips still work during playback.
    const knownDur = getActiveDuration();
    const upperBound = knownDur > 0 ? knownDur : Infinity;
    const next = Math.max(0, Math.min(upperBound, video.currentTime + seconds));
    if (Number.isFinite(next)) {
      video.currentTime = next;
      setCurrentTime(next);
    }
  };

  const changeSpeed = (speed: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackRate(speed);
      setShowSpeedOptions(false);
    }
  };

  const handleMouseUp = useCallback(() => {
    if (!isSeeking || !videoRef.current) return;
    videoRef.current.currentTime = seekTimeRef.current;
    setIsSeeking(false);
    if (isPlaying) {
      videoRef.current.play().catch(console.error);
    }
  }, [isSeeking, isPlaying]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      // Request fullscreen on the container div (not the <video>) so that
      // the custom controls overlay remains visible inside the fullscreen view.
      containerRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePip = async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled && videoRef.current) {
        await videoRef.current.requestPictureInPicture();
      }
    } catch (err) {
      console.error('Error toggling Picture-in-Picture:', err);
    }
  };

  useEffect(() => {
    const hint = knownDuration != null ? Number(knownDuration) : 0;
    if (hint > 0 && hint !== 60) setDuration(hint);
  }, [knownDuration]);

  // ── Server-side duration probe ─────────────────────────────────────────────
  // When the video has no stored duration AND the browser reports Infinity (WebM),
  // query the backend ffprobe endpoint. This is fast (< 3 s) and non-blocking.
  useEffect(() => {
    if (!videoId) return;
    const hint = knownDuration != null ? Number(knownDuration) : 0;
    if (isUsableDuration(hint)) return; // already known

    let cancelled = false;

    // Retry schedule: 1 s → 15 s → 30 s → 60 s (for WebM full-scan on server)
    const RETRY_DELAYS = [1_000, 15_000, 30_000, 60_000];

    const probe = async (attempt: number) => {
      if (cancelled) return;
      try {
        const base = (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8080';
        const response = await fetch(`${base}/api/videos/${videoId}/probe-duration`);
        if (cancelled || !response.ok) return;
        const json = await response.json() as { duration?: number; source?: string };
        const probed = Number(json.duration ?? 0);
        if (isUsableDuration(probed)) {
          if (learnedRef.current.src === src) learnedRef.current.seconds = probed;
          knownGoodDurationRef.current = probed;
          setDuration(probed);
          onDurationKnown?.(Math.round(probed));
          return; // done
        }
        // Server is still scanning (WebM without duration header) — retry later
        const next = RETRY_DELAYS[attempt + 1];
        if (next !== undefined) {
          timers.push(setTimeout(() => probe(attempt + 1), next));
        }
      } catch (_) { /* player still works without duration */ }
    };

    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => probe(0), RETRY_DELAYS[0]));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [videoId, knownDuration, src, onDurationKnown]);
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset guards whenever the listeners are set up again (a new source, or a seek); what this
    // source already taught us (learnedRef) is kept.
    knownGoodDurationRef.current = 0;
    lastKnownTimeRef.current = 0;
    if (learnedRef.current.src !== src) learnedRef.current = { src, seconds: 0, wrongHint: 0 };
    const learned = learnedRef.current;

    // If the caller already knows the duration from the DB, trust it immediately (unless this
    // source already gave its real length, or proved the stored one wrong).
    // This pre-seeds knownGoodDurationRef so no network round-trip or seek operation is needed.
    const hintSecs = knownDuration != null ? Number(knownDuration) : 0;
    if (learned.seconds > 0) {
      knownGoodDurationRef.current = learned.seconds;
      setDuration(learned.seconds);
    } else if (isUsableDuration(hintSecs) && hintSecs !== learned.wrongHint) {
      knownGoodDurationRef.current = hintSecs;
      setDuration(hintSecs);
    }

    const applyDuration = (seconds: number) => {
      if (!isUsableDuration(seconds)) return;
      learned.seconds = seconds;
      // Avoid downgrading a previously locked-in good duration.
      if (knownGoodDurationRef.current > 0 && Math.abs(seconds - knownGoodDurationRef.current) < 1) return;
      knownGoodDurationRef.current = seconds;
      setDuration(seconds);
      onDurationKnown?.(Math.round(seconds));
    };

    const syncDuration = () => {
      // The file's own length wins over the stored one: a recording made before the recorder read
      // its time from the clock can carry a stored length that is too short. Infinity is never
      // taken (a WebM recorded with MediaRecorder has no length until it is read through, and
      // fullscreen transitions report it for a moment), so a good value stays; without one, the
      // length is inferred when the video ends (see handleEnded).
      if (isUsableDuration(video.duration)) {
        applyDuration(video.duration);
      }
    };

    const handleTimeUpdate = () => {
      if (scanRef.current) return; // reading the file through: the position is meaningless
      const t = video.currentTime;
      if (!isSeeking) {
        setCurrentTime(t);
      }
      // Track the highest position seen so handleEnded can infer the duration.
      if (t > lastKnownTimeRef.current) {
        lastKnownTimeRef.current = t;
      }
      // Playing past the stored length proves it wrong (a file without its own length falls back
      // to it): drop it, so the total is not shown shorter than the position, and the real length
      // is taken when the video ends.
      if (!isUsableDuration(video.duration) && knownGoodDurationRef.current > 0 && t > knownGoodDurationRef.current + 1) {
        learned.wrongHint = knownGoodDurationRef.current;
        knownGoodDurationRef.current = 0;
        setDuration(0);
      }
    };

    const handleEnded = () => {
      if (scanRef.current) return; // the read-through reaches the end; the viewer's video has not ended
      setIsPlaying(false);
      // If the browser never resolved the duration (WebM Infinity case), infer it
      // from the last timeupdate position — that position IS the actual duration.
      if (knownGoodDurationRef.current === 0) {
        const inferred = lastKnownTimeRef.current;
        if (isUsableDuration(inferred)) {
          applyDuration(inferred);
        }
      }
      onEnded?.();
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handlePlay = () => { setIsPlaying(true); onPlay?.(); };
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('loadedmetadata', syncDuration);
    video.addEventListener('durationchange', syncDuration);
    video.addEventListener('canplay', syncDuration);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    syncDuration();

    return () => {
      video.removeEventListener('loadedmetadata', syncDuration);
      video.removeEventListener('durationchange', syncDuration);
      video.removeEventListener('canplay', syncDuration);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [src, isSeeking, onDurationKnown, knownDuration]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;
      // Ignore when focus is inside any text-entry element (input, textarea, contenteditable)
      if (
        active?.tagName === 'INPUT' ||
        active?.tagName === 'TEXTAREA' ||
        (active as HTMLElement)?.isContentEditable
      ) return;

      switch (e.key.toLowerCase()) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'arrowright':
          e.preventDefault();
          skip(5);
          break;
        case 'arrowleft':
          e.preventDefault();
          skip(-5);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying]);

  useEffect(() => {
    const moveHandler = (e: MouseEvent) => handleMouseMove(e);
    const upHandler = () => handleMouseUp();

    if (isSeeking) {
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
    }

    return () => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };
  }, [isSeeking, handleMouseMove, handleMouseUp]);

  const videoSrc = src || '';
  const resolvedMime = guessMimeType(videoSrc, mimeType);

  // A failed address is reported once; the position is kept for the fresh one.
  const failedSrcRef = useRef<string | null>(null);
  const resumeRef = useRef<{ time: number; playing: boolean } | null>(null);
  const handleSourceError = () => {
    if (!onSourceError || !videoSrc || failedSrcRef.current === videoSrc) return;
    failedSrcRef.current = videoSrc;
    const v = videoRef.current;
    resumeRef.current = { time: v?.currentTime ?? 0, playing: !!v && !v.paused };
    onSourceError();
  };
  useEffect(() => {
    const v = videoRef.current;
    const resume = resumeRef.current;
    if (!v || !resume) return;
    const onMeta = () => {
      resumeRef.current = null;
      if (resume.time > 0) v.currentTime = resume.time;
      if (resume.playing) void v.play().catch(() => undefined);
    };
    v.addEventListener('loadedmetadata', onMeta, { once: true });
    return () => v.removeEventListener('loadedmetadata', onMeta);
  }, [videoSrc]);

  // A WebM recorded in the browser (before the backend started adding a seek index on upload)
  // has no index and no duration: Chrome can seek only within the part it has already read and
  // restarts at 0 beyond it. Such a seek — from the bar, the keys or a comment's time — is
  // turned into a seek "past the end", which makes Chrome read the file through (and learn its
  // duration); it then lands at the asked-for time. Indexed files never come here.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const loadedEnd = () => {
      let end = 0;
      for (let i = 0; i < video.buffered.length; i++) end = Math.max(end, video.buffered.end(i));
      return end;
    };
    const onSeeking = () => {
      if (scanRef.current || Number.isFinite(video.duration)) return;
      const target = video.currentTime;
      if (target <= loadedEnd()) return;
      scanRef.current = { target, resume: !video.paused, landing: false };
      setCurrentTime(target);
      video.currentTime = 1e10;
    };
    const onSeeked = () => {
      const scan = scanRef.current;
      if (!scan) return;
      if (!scan.landing) {
        scan.landing = true;
        video.currentTime = scan.target;
        return;
      }
      scanRef.current = null;
      if (scan.resume) void video.play().catch(() => undefined);
    };
    // A read-through that fails (e.g. the link expired meanwhile) must not leave the player deaf.
    const onError = () => { scanRef.current = null; };
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    return () => {
      scanRef.current = null;
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
  }, [videoSrc]);

  // The <video> is re-created when the source changes (key), so share it again each time.
  useEffect(() => {
    if (!videoElementRef) return;
    videoElementRef.current = videoRef.current;
    return () => { videoElementRef.current = null; };
  }, [videoElementRef, videoSrc]);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={{
        backgroundColor: '#000',
        ...style,
      }}
    >
      <video
        ref={videoRef}
        key={videoSrc}
        className="w-full h-full"
        // Keeps the player's height limit (e.g. 80vh in My Library): a portrait video fits within it.
        style={{ display: 'block', maxHeight: 'inherit' }}
        poster={poster}
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
        playsInline
        preload="metadata"
        onError={handleSourceError}
      >
        {videoSrc ? <source src={videoSrc} type={resolvedMime} onError={handleSourceError} /> : null}
        {t('video_player.browser_unsupported')}
      </video>

      {/* Click overlay — only shows the center play button when paused */}
      <div
        className="absolute inset-0 flex items-center justify-center cursor-pointer"
        onClick={togglePlay}
      >
        {!isPlaying && (
          <button
            className="p-4 bg-white bg-opacity-80 rounded-full"
            aria-label={t('video_player.play')}
          >
            <Play size={24} className="text-black" />
          </button>
        )}
      </div>

      {/*
        Controls bar — shown via CSS :hover on THIS element only (not the whole video).
        Using a tall transparent top-padding (pb-12) so the hover zone extends ~48px
        above the visible gradient, making it comfortable to reach without needing
        pixel-perfect precision. When paused the bar is always visible.
      */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 transition-opacity duration-200 ${
          isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'
        }`}
        style={{ paddingTop: '3rem' }}
      >
      {(() => {
        const hasDuration = getActiveDuration() > 0;
        // Allow scrubbing also when we've seen some of the video (backward seek)
        const canSeek = hasDuration || lastKnownTimeRef.current > 0;
        return (
          <div
            ref={progressRef}
            className={`w-full h-2 bg-gray-600 rounded-full mb-2 ${canSeek ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={(e) => { if (canSeek) handleScrub(e, true); }}
            onMouseDown={(e) => {
              if (canSeek) {
                setIsSeeking(true);
                handleScrub(e, false);
              }
            }}
          >
            <div
              className="h-full bg-indigo-600 rounded-full relative transition-all duration-100"
              style={{ width: `${getProgressPercentage()}%` }}
            >
              {canSeek && (
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow" />
              )}
            </div>
          </div>
        );
      })()}

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={togglePlay}
              className="p-2 text-white hover:bg-white/20 rounded-full"
              aria-label={isPlaying ? t('video_player.pause') : t('video_player.play')}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>

            <button
              onClick={() => skip(-5)}
              className="hidden sm:block p-2 text-white hover:bg-white/20 rounded-full"
              aria-label={t('video_player.skip_back_5s')}
            >
              <div className="relative w-5 h-5">
                <RotateCcw size={20} className="absolute" />
                <span className="absolute text-xs font-medium -bottom-1 -right-1">5</span>
              </div>
            </button>

            <button
              onClick={() => skip(5)}
              className="hidden sm:block p-2 text-white hover:bg-white/20 rounded-full"
              aria-label={t('video_player.skip_forward_5s')}
            >
              <div className="relative w-5 h-5">
                <RotateCw size={20} className="absolute" />
                <span className="absolute text-xs font-medium -bottom-1 -right-1">5</span>
              </div>
            </button>

            <div className="flex items-center group">
              <button
                onClick={toggleMute}
                className="p-2 text-white hover:bg-white/20 rounded-full"
                aria-label={isMuted ? t('video_player.unmute') : t('video_player.mute')}
              >
                {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
              <div className="w-0 opacity-0 group-hover:w-20 group-hover:opacity-100 transition-all duration-200 overflow-hidden">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-full h-1 bg-gray-600 rounded-full appearance-none cursor-pointer"
                />
              </div>
            </div>

            <div className="text-white text-sm font-mono whitespace-nowrap">
              {`${formatTime(currentTime)} / ${getActiveDuration() > 0 ? formatTime(getActiveDuration()) : '--:--'}`}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={() => setShowSpeedOptions(!showSpeedOptions)}
                className="p-2 text-white hover:bg-white/20 rounded-full text-sm font-medium"
                aria-label={t('video_player.playback_speed')}
              >
                {playbackRate}x
              </button>
              {showSpeedOptions && (
                <div className="absolute bottom-full right-0 mb-2 w-20 bg-gray-900 rounded-md shadow-lg z-10">
                  {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3].map(speed => (
                    <button
                      key={speed}
                      onClick={() => changeSpeed(speed)}
                      className={`block w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700 ${playbackRate === speed ? 'bg-indigo-600' : ''}`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={togglePip}
              className="hidden sm:block p-2 text-white hover:bg-white/20 rounded-full"
              aria-label={t('video_player.picture_in_picture')}
            >
              <PictureInPicture size={20} />
            </button>

            <button
              onClick={toggleFullscreen}
              className="p-2 text-white hover:bg-white/20 rounded-full"
              aria-label={isFullscreen ? t('video_player.exit_fullscreen') : t('video_player.enter_fullscreen')}
            >
              <Maximize size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
